/**
 * Los recursos con los que se arma el SISTEMA DE DISEÑO en la puesta en marcha:
 * repos de GitHub, carpetas de código, archivos de Figma, tipografías y assets.
 *
 * LÓGICA PURA, sin React ni DOM: acá adentro no entra un `File` ni un `URL` de
 * la pantalla, entran los datos que se pueden sacar de ellos. Mismo criterio que
 * `materialesConocimiento.ts`, y por el mismo motivo: lo único que no puede
 * fallar de esta pantalla —qué link es un repo y qué link no lo es, qué archivo
 * es un `.fig`, qué carpeta se eligió— se testea sin montar nada.
 *
 * LO QUE PASA CON LO QUE SE CARGA ACÁ. No hay backend para el sistema de
 * diseño: NADA se sube y NADA se guarda todavía. De un archivo se anota lo que
 * `anotarArchivo` sabe anotar (nombre, tamaño, formato) y de una carpeta su
 * nombre y cuántos archivos tiene. Lo cargado vive en el estado del paso y se
 * pierde al salir de la pantalla. Ver
 * ADR-2026-08-17-estudio-marketing-paso-sistema-diseno.
 */

import { formatearTamano, type MaterialArchivo } from "./materialesConocimiento";
import { idEstable } from "./idEstable";

/**
 * Lo que la pantalla dibuja de CUALQUIER recurso cargado: un nombre y una línea
 * que lo describe. Es la forma común a la que se llevan las cuatro cosas
 * (repos, carpetas, `.fig`, assets) para que la lista de abajo de cada fila sea
 * un solo componente y no cuatro casi iguales.
 */
export interface RecursoSistemaDiseno {
  /** Determinista: volver a cargar lo mismo dedupea en vez de duplicar. */
  id: string;
  nombre: string;
  detalle: string;
}

/* ── Repos de GitHub ────────────────────────────────────────────────────── */

export interface RepoGithub {
  id: string;
  /** El usuario o la organización: `estudio-atlas`. */
  duenio: string;
  /** El nombre del repo, sin `.git`. */
  repo: string;
  /** Canónica: `https://github.com/duenio/repo`, sin `/tree/…` ni `.git`. */
  url: string;
}

export type MotivoRepo = "vacio" | "no-es-url" | "no-es-github" | "sin-repo";

export const MENSAJE_REPO: Record<MotivoRepo, string> = {
  vacio: "Pegá el link del repo.",
  "no-es-url": "Eso no parece un link. Se ve así: github.com/tu-usuario/tu-repo",
  // No dice "es inválido": es un link perfectamente bueno, de otro lado. Lo que
  // falta es soporte nuestro, y eso es información distinta.
  "no-es-github": "Por ahora sólo podemos leer repos de GitHub.",
  "sin-repo": "Falta el repo: con el usuario solo no alcanza (github.com/tu-usuario/tu-repo).",
};

export type LecturaRepo = { ok: true; repo: RepoGithub } | { ok: false; motivo: MotivoRepo };

/** Lo que ofrece el botón "Code" de GitHub cuando se elige SSH. Se pega tal
 *  cual mucho más seguido de lo que uno esperaría. */
const SSH_GITHUB = /^git@github\.com:(.+)$/i;

/** Lo que GitHub acepta como nombre de usuario y de repo. Sirve para no aceptar
 *  como repo un pedazo de URL que trae caracteres que ahí no pueden estar. */
const NOMBRE_VALIDO = /^[A-Za-z0-9._-]+$/;

/**
 * `duenio/repo` a partir del camino de la URL. Se queda con los DOS primeros
 * segmentos y tira el resto: `github.com/atlas/web/tree/main/src` y
 * `github.com/atlas/web` son el mismo repo, y sin esto entrarían dos veces.
 */
function repoDesdeCamino(camino: string): LecturaRepo {
  const partes = camino.split("/").filter(Boolean);
  const duenio = partes[0];
  const crudo = partes[1];
  // Un segmento solo (`github.com/atlas`) es un perfil, no un repo. Y sin
  // segmentos es la home de GitHub.
  if (!duenio || !crudo) return { ok: false, motivo: "sin-repo" };
  const repo = crudo.replace(/\.git$/i, "");
  if (!NOMBRE_VALIDO.test(duenio) || !repo || !NOMBRE_VALIDO.test(repo)) {
    return { ok: false, motivo: "no-es-url" };
  }
  return {
    ok: true,
    repo: {
      // En minúsculas para el id (GitHub no distingue mayúsculas al resolver el
      // repo) pero se muestra como lo escribió el usuario.
      id: idEstable("repo-github", `${duenio.toLowerCase()}/${repo.toLowerCase()}`),
      duenio,
      repo,
      url: `https://github.com/${duenio}/${repo}`,
    },
  };
}

/**
 * El link de un repo de GitHub, en cualquiera de las formas en que se pega:
 * completa, sin esquema, con `.git`, con `/tree/rama/carpeta` atrás o en SSH.
 *
 * Los rechazos son explícitos y distintos entre sí a propósito: "no es un link"
 * y "es un link, pero de GitLab" mandan al usuario a hacer cosas distintas.
 */
export function interpretarRepoGithub(crudo: string): LecturaRepo {
  const texto = crudo.trim();
  if (!texto) return { ok: false, motivo: "vacio" };

  const ssh = texto.match(SSH_GITHUB);
  if (ssh) return repoDesdeCamino(ssh[1]);

  const esHttp = /^https?:\/\//i.test(texto);
  // Mismo criterio que `interpretarPaginaWeb`: se completa el esquema que
  // falta, pero NUNCA se le antepone `https://` a algo que ya trae otro.
  if (/^[a-z][a-z0-9+.-]*:/i.test(texto) && !esHttp) return { ok: false, motivo: "no-es-url" };

  let url: URL;
  try {
    url = new URL(esHttp ? texto : `https://${texto}`);
  } catch {
    return { ok: false, motivo: "no-es-url" };
  }

  const host = url.hostname.toLowerCase().replace(/^www\./, "");
  // Sin punto no es un dominio: `new URL("https://hola")` parsea sin chistar y
  // diría "es de otro sitio" sobre algo que ni siquiera es una dirección.
  if (!host.includes(".")) return { ok: false, motivo: "no-es-url" };
  if (host !== "github.com") return { ok: false, motivo: "no-es-github" };

  return repoDesdeCamino(url.pathname);
}

export const recursoDeRepo = (repo: RepoGithub): RecursoSistemaDiseno => ({
  id: repo.id,
  nombre: `${repo.duenio}/${repo.repo}`,
  detalle: "Repositorio de GitHub",
});

/* ── Archivos de Figma ──────────────────────────────────────────────────── */

/** Un `.fig` es el archivo local de Figma. Se chequea la extensión y no el MIME
 *  porque el navegador manda `application/octet-stream` (o nada) para ese
 *  formato: el MIME no distingue un `.fig` de cualquier binario. */
export const esArchivoFig = (nombre: string) => /\.fig$/i.test(nombre.trim());

/* ── Carpetas de código ─────────────────────────────────────────────────── */

export interface CarpetaElegida {
  nombre: string;
  /** Cuántos archivos trae adentro, contando subcarpetas. */
  archivos: number;
}

/**
 * La carpeta que se eligió, leída de los `webkitRelativePath` de sus archivos
 * (`carpeta/src/App.tsx`). Devuelve `null` cuando eso NO fue una carpeta:
 *
 *  · archivos sueltos (el `webkitRelativePath` viene vacío), que es lo que pasa
 *    cuando se ARRASTRA en vez de elegir con el buscador;
 *  · varias raíces distintas, que el selector de carpeta no produce.
 *
 * El `null` no es un detalle: la pantalla lo usa para decirle al usuario que
 * arrastrando una carpeta el navegador no deja leer lo que tiene adentro, en
 * vez de anotar una carpeta vacía que no existe.
 */
export function carpetaDeRutas(rutas: readonly string[]): CarpetaElegida | null {
  const limpias = rutas.map((ruta) => ruta.trim()).filter(Boolean);
  if (limpias.length === 0) return null;
  const raices = new Set<string>();
  for (const ruta of limpias) {
    const segmentos = ruta.split("/").filter(Boolean);
    // Sin al menos un nivel adentro no hay carpeta que nombrar.
    if (segmentos.length < 2) return null;
    raices.add(segmentos[0]);
  }
  if (raices.size !== 1) return null;
  return { nombre: [...raices][0], archivos: limpias.length };
}

export const recursoDeCarpeta = (carpeta: CarpetaElegida): RecursoSistemaDiseno => ({
  id: idEstable("carpeta-codigo", `${carpeta.nombre}::${carpeta.archivos}`),
  nombre: carpeta.nombre,
  detalle: `Carpeta · ${carpeta.archivos} ${carpeta.archivos === 1 ? "archivo" : "archivos"}`,
});

/* ── Archivos sueltos ───────────────────────────────────────────────────── */

/**
 * Un archivo ya anotado, en forma de recurso. Reusa `anotarArchivo` (y por lo
 * tanto su id determinista y su formateo de tamaño) en vez de repetir esa
 * lógica: es el mismo problema resuelto en la base de conocimiento.
 */
export const recursoDeArchivo = (material: MaterialArchivo): RecursoSistemaDiseno => ({
  id: material.id,
  nombre: material.nombre,
  detalle: `${material.formato} · ${formatearTamano(material.bytes)}`,
});

/* ── Agregado sin repetir ───────────────────────────────────────────────── */

export interface ResumenCarga<T> {
  lista: T[];
  /** Los que entraron de verdad, en orden. Es lo que se puede nombrar en un
   *  aviso sin mentir: si el primero de la tanda ya estaba, nombrarlo diría que
   *  se agregó justo el que no. */
  nuevos: T[];
  duplicados: number;
}

/**
 * Agrega sin mutar y sin repetir por id. Es UNA función para las cuatro listas
 * (repos, código, `.fig`, assets) porque el problema es el mismo en las cuatro,
 * y cuatro copias es donde una arregla un bug y las otras tres no.
 */
export function agregarSinRepetir<T extends { id: string }>(
  lista: readonly T[],
  nuevos: readonly T[],
): ResumenCarga<T> {
  const vistos = new Set(lista.map((item) => item.id));
  const resultado = [...lista];
  const entraron: T[] = [];
  let duplicados = 0;
  for (const item of nuevos) {
    if (vistos.has(item.id)) {
      duplicados += 1;
      continue;
    }
    vistos.add(item.id);
    resultado.push(item);
    entraron.push(item);
  }
  return { lista: resultado, nuevos: entraron, duplicados };
}

export const quitarRecurso = <T extends { id: string }>(lista: readonly T[], id: string): T[] =>
  lista.filter((item) => item.id !== id);
